/**
 * ISO 4217 currency table — the ones our ops team is likely to book
 * in. Curated (~60 entries) rather than the full ~180 so the dropdown
 * stays scannable. Anything not here can still be entered as a free-text
 * 3-letter code via CurrencySelect's "Add other code" action — the DB
 * column is a plain varchar(3), so any code round-trips fine.
 */
export interface CurrencyInfo {
  code: string;       // ISO 4217 uppercase
  name: string;       // human-readable
  symbol?: string;    // used in the debit ledger + notifications
}

export const CURRENCIES: readonly CurrencyInfo[] = [
  // Home + primary tier
  { code: 'INR', name: 'Indian Rupee',            symbol: '₹' },

  // Global reserves + North America
  { code: 'USD', name: 'US Dollar',                symbol: '$' },
  { code: 'EUR', name: 'Euro',                    symbol: '€' },
  { code: 'GBP', name: 'Pound Sterling',          symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar',         symbol: 'C$' },
  { code: 'MXN', name: 'Mexican Peso',            symbol: 'Mex$' },

  // Asia-Pacific (heavy for FGF bookings)
  { code: 'JPY', name: 'Japanese Yen',            symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan',            symbol: '¥' },
  { code: 'HKD', name: 'Hong Kong Dollar',        symbol: 'HK$' },
  { code: 'SGD', name: 'Singapore Dollar',        symbol: 'S$' },
  { code: 'AUD', name: 'Australian Dollar',       symbol: 'A$' },
  { code: 'NZD', name: 'New Zealand Dollar',      symbol: 'NZ$' },
  { code: 'KRW', name: 'South Korean Won',        symbol: '₩' },
  { code: 'TWD', name: 'New Taiwan Dollar',       symbol: 'NT$' },
  { code: 'THB', name: 'Thai Baht',               symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit',       symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah',       symbol: 'Rp' },
  { code: 'VND', name: 'Vietnamese Dong',         symbol: '₫' },
  { code: 'PHP', name: 'Philippine Peso',         symbol: '₱' },
  { code: 'BDT', name: 'Bangladeshi Taka',        symbol: '৳' },
  { code: 'LKR', name: 'Sri Lankan Rupee',        symbol: 'Rs' },
  { code: 'NPR', name: 'Nepalese Rupee',          symbol: 'रु' },
  { code: 'PKR', name: 'Pakistani Rupee',         symbol: '₨' },
  { code: 'MMK', name: 'Myanmar Kyat',            symbol: 'K' },
  { code: 'KHR', name: 'Cambodian Riel',          symbol: '៛' },
  { code: 'LAK', name: 'Lao Kip',                 symbol: '₭' },
  { code: 'BND', name: 'Brunei Dollar',           symbol: 'B$' },
  { code: 'MOP', name: 'Macanese Pataca',         symbol: 'MOP$' },
  { code: 'MNT', name: 'Mongolian Tögrög',        symbol: '₮' },

  // Middle East + South Asia border
  { code: 'AED', name: 'UAE Dirham',              symbol: 'AED' },
  { code: 'SAR', name: 'Saudi Riyal',             symbol: '﷼' },
  { code: 'QAR', name: 'Qatari Riyal',            symbol: 'QR' },
  { code: 'OMR', name: 'Omani Rial',              symbol: 'OMR' },
  { code: 'BHD', name: 'Bahraini Dinar',          symbol: 'BD' },
  { code: 'KWD', name: 'Kuwaiti Dinar',           symbol: 'KD' },
  { code: 'JOD', name: 'Jordanian Dinar',         symbol: 'JD' },
  { code: 'ILS', name: 'Israeli Shekel',          symbol: '₪' },
  { code: 'TRY', name: 'Turkish Lira',            symbol: '₺' },
  { code: 'EGP', name: 'Egyptian Pound',          symbol: 'E£' },
  { code: 'IRR', name: 'Iranian Rial',            symbol: '﷼' },

  // Europe (non-Euro)
  { code: 'CHF', name: 'Swiss Franc',             symbol: 'CHF' },
  { code: 'NOK', name: 'Norwegian Krone',         symbol: 'kr' },
  { code: 'SEK', name: 'Swedish Krona',           symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone',            symbol: 'kr' },
  { code: 'PLN', name: 'Polish Złoty',            symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna',            symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint',        symbol: 'Ft' },
  { code: 'RON', name: 'Romanian Leu',            symbol: 'lei' },
  { code: 'RUB', name: 'Russian Rouble',          symbol: '₽' },
  { code: 'UAH', name: 'Ukrainian Hryvnia',       symbol: '₴' },
  { code: 'ISK', name: 'Icelandic Króna',         symbol: 'kr' },

  // Africa
  { code: 'ZAR', name: 'South African Rand',      symbol: 'R' },
  { code: 'KES', name: 'Kenyan Shilling',         symbol: 'KSh' },
  { code: 'TZS', name: 'Tanzanian Shilling',      symbol: 'TSh' },
  { code: 'NGN', name: 'Nigerian Naira',          symbol: '₦' },
  { code: 'MAD', name: 'Moroccan Dirham',         symbol: 'DH' },
  { code: 'MUR', name: 'Mauritian Rupee',         symbol: '₨' },
  { code: 'ETB', name: 'Ethiopian Birr',          symbol: 'Br' },

  // Latin America
  { code: 'BRL', name: 'Brazilian Real',          symbol: 'R$' },
  { code: 'ARS', name: 'Argentine Peso',          symbol: 'AR$' },
  { code: 'CLP', name: 'Chilean Peso',            symbol: 'CLP$' },
  { code: 'COP', name: 'Colombian Peso',          symbol: 'COL$' },
  { code: 'PEN', name: 'Peruvian Sol',            symbol: 'S/' },
] as const;

const CODE_TO_INFO: Record<string, CurrencyInfo> = Object.fromEntries(
  CURRENCIES.map((c) => [c.code, c]),
);

export function currencyInfo(code: string | null | undefined): CurrencyInfo | null {
  if (!code) return null;
  return CODE_TO_INFO[code.toUpperCase()] ?? null;
}

export function currencyLabel(code: string | null | undefined): string {
  if (!code) return '';
  const info = currencyInfo(code);
  return info ? `${info.code} — ${info.name}` : code.toUpperCase();
}

/** Validate a free-text ISO code the ops user types when their currency
 *  isn't in the curated list. Enforces the ISO 4217 shape (3 uppercase
 *  letters) without pretending we know every code that exists. */
export function isValidCurrencyCode(raw: string): boolean {
  return /^[A-Z]{3}$/.test(raw.trim().toUpperCase());
}
